import React, { createContext, useState, useEffect, useContext } from "react";
import axiosInstance from './axiosInstance';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedRefreshToken = localStorage.getItem("refreshToken");

    if (storedToken && storedRefreshToken) {
      setToken(storedToken);
      setRefreshToken(storedRefreshToken);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('user/');
        setUser(response.data);
        const adminFlag = response.data.is_staff || response.data.is_superuser;
        setIsAdmin(!!adminFlag);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setUser(null);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUser();
    } else {
      setUser(null);
      setIsAdmin(false);
      setLoading(false);
    }
  }, [token]);

  const logoutUser = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setIsAdmin(false);
  };

  const refreshAccessToken = async () => {
    try {
      const response = await axiosInstance.post(
        "token/refresh/",
        { refresh: refreshToken },
        { headers: { "Content-Type": "application/json" } }
      );

      const newAccessToken = response.data.access;
      setToken(newAccessToken);
      localStorage.setItem("token", newAccessToken);
      console.log("Token refreshed!");
    } catch (error) {
      console.error("Error refreshing access token:", error);
      logoutUser();
      console.log("User logged out.");
    }
  };

  useEffect(() => {
    if (token) {
      console.log("Token set:", token);
    } else {
      console.log("Token cleared");
    }
  }, [token]);

  const isAuthenticated = () => {
    return token !== null;
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        refreshToken,
        setRefreshToken,
        logoutUser,
        loading,
        isAuthenticated,
        refreshAccessToken,
        user,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  return useContext(AuthContext);
};

export { useAuth };
